import "./AboutMe.scss";
import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { MetaData } from "../../helpers/MetaData";
import JsonLd from "../../helpers/JsonLD";
import { socialPreviews } from "../../utils/socialPreviews";

const canonicalUrl = "https://alenalobacheva.com/about/";
const portraitUrl = "https://cdn.alenalobacheva.com/staticPhotos/aboutMe-image.webp";
const businessId = "https://alenalobacheva.com/#localbusiness";
const personId = `${canonicalUrl}#alena-lobacheva`;

const aboutPageData = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${canonicalUrl}#webpage`,
  url: canonicalUrl,
  name: "About Alena Lobacheva | Charlotte Newborn Photographer",
  description:
    "Meet Alena Lobacheva, a Charlotte newborn and family photographer who has worked with babies and families since 2017.",
  image: portraitUrl,
  mainEntity: {
    "@id": personId,
  },
};

const personData = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": personId,
  name: "Alena Lobacheva",
  url: canonicalUrl,
  image: portraitUrl,
  jobTitle: "Newborn and Family Photographer",
  worksFor: {
    "@id": businessId,
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Charlotte",
    addressRegion: "NC",
    addressCountry: "US",
  },
  sameAs: [
    "https://www.instagram.com/lobachevaphotography/",
    "https://www.facebook.com/Alen4ikLobacheva?mibextid=9R9pXO",
    "https://www.tiktok.com/@lobachevaphotography/",
  ],
};

const localBusinessData = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": businessId,
  name: "Alena Lobacheva Photography",
  description:
    "Newborn, maternity, baby, and family photography in Charlotte, North Carolina and surrounding communities.",
  image: portraitUrl,
  url: "https://alenalobacheva.com/",
  telephone: "+1-516-468-4837",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Charlotte",
    addressRegion: "NC",
    addressCountry: "US",
  },
  founder: {
    "@id": personId,
  },
  sameAs: personData.sameAs,
};

export const AboutMe: React.FC = () => {
  return (
    <Fragment>
      <JsonLd data={aboutPageData} />
      <JsonLd data={personData} />
      <JsonLd data={localBusinessData} />
      <MetaData
        title="About Alena Lobacheva | Charlotte Newborn Photographer"
        description="Meet Alena Lobacheva, a Charlotte newborn and family photographer serving Concord, Ballantyne, Rock Hill, Mooresville, and nearby communities."
        canonicalLink={canonicalUrl}
        {...socialPreviews.about}
        imageAltOG="Alena Lobacheva, newborn photographer in Charlotte, NC"
        titleOG="About Alena Lobacheva | Charlotte Newborn Photographer"
        descriptionOG="The story behind Alena Lobacheva Photography and a gentle, baby-led approach to newborn and family photography."
      />

      <main className="aboutMe">
        <section className="aboutMe__intro" aria-labelledby="about-alena-title">
          <div className="aboutMe__photoFrame">
            <img
              className="aboutMe__photo"
              src={portraitUrl}
              width="2048"
              height="2048"
              loading="lazy"
              decoding="async"
              alt="Alena Lobacheva, newborn photographer in Charlotte, NC"
            />
          </div>

          <div className="aboutMe__copy">
            <p className="aboutMe__eyebrow">
              <span className="aboutMe__name">Alena Lobacheva</span>
              <span className="aboutMe__profession">Charlotte newborn photographer</span>
            </p>
            <h1 className="aboutMe__title" id="about-alena-title">
              The story behind the lens
            </h1>
            <p className="aboutMe__lead">I create calm, meaningful portraits of the people you love most.</p>
            <p className="aboutMe__text">
              My journey into newborn photography began with motherhood. After my son was born, I understood how quickly
              the smallest details change—and how deeply a photograph can bring those moments back.
            </p>
            <p className="aboutMe__text">
              For more than eight years, I have worked closely with babies and families, creating a gentle experience
              shaped around each child. There is no pressure to perform and no need for everything to go perfectly. We
              follow your baby&apos;s pace.
            </p>
            <p className="aboutMe__text">
              I serve families throughout Charlotte, Concord, Ballantyne, Rock Hill, Mooresville and nearby communities,
              and I would be honored to preserve this chapter of your family&apos;s story.
            </p>
          </div>
        </section>

        <section className="aboutMe__trust" aria-label="About Alena highlights">
          <div className="aboutMe__trustItem">
            <span className="aboutMe__trustValue">Since 2017</span>
            <span className="aboutMe__trustLabel">Photographing babies and families</span>
          </div>
          <div className="aboutMe__trustItem">
            <span className="aboutMe__trustValue">Baby-led</span>
            <span className="aboutMe__trustLabel">Patient and gentle approach</span>
          </div>
          <div className="aboutMe__trustItem">
            <span className="aboutMe__trustValue">Charlotte, NC</span>
            <span className="aboutMe__trustLabel">Serving surrounding communities</span>
          </div>
        </section>

        <section className="aboutMe__closing" aria-labelledby="about-closing-title">
          <p className="aboutMe__closingEyebrow">A little more than photographs</p>
          <h2 className="aboutMe__closingTitle" id="about-closing-title">
            Your family can simply be together.
          </h2>
          <p className="aboutMe__closingText">
            You do not need to arrive knowing how to pose or with every detail planned. I will gently guide you and
            leave room for the honest moments in between.
          </p>
          <Link className="aboutMe__contactLink" to="/contact/">
            Start a conversation <span aria-hidden="true">→</span>
          </Link>
        </section>
      </main>
    </Fragment>
  );
};
